import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { path } from 'ramda';
import { Link } from 'react-router-dom';

import { Card, CardHeader, CardContent, Collapse, Typography, Tooltip, IconButton } from '@material-ui/core';
import MTable from '../../../components/MTable';
import TableMobile from '../../../components/TableMobile';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import Invoice from '../../../components/Invoice';
import InvoicePos from '../../../components/InvoicePos';
import InvoiceServiceContract from '../../../components/InvoiceServiceContract';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { loadClientInvoices } from '../store/thunk';
import { setClientId } from '../../ClientRegistration/components/steps/BasicInfo/store/actions';
import { setInvoiceId, handleUpdateInvoice } from '../store/actions';
import { loadFilters } from '../../../components/Filter/store/thunk';

import { convertToBrl } from 'helpers/converters';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { useStyles } from './styles';

const headers = [
  { title: 'Data do vencimento', field: 'dueDateTime', sortable: false },
  { title: 'Data do recebimento', field: 'paymentDateTime', sortable: false },
  { title: 'Situação', field: 'invoiceSituation', sortable: false },
  { title: 'Valor', field: 'amountTotal', sortable: false }
];

const invoicePosHeaders = [
  { title: 'Data do vencimento', field: 'dueDateTime', sortable: false },
  { title: 'Data do fechamento', field: 'closingDateTime', sortable: false },
  { title: 'Data do recebimento', field: 'paymentDateTime', sortable: false },
  { title: 'Situação', field: 'invoiceSituation', sortable: false },
  { title: 'Valor', field: 'amountTotal', sortable: false }
];

const width = window.innerWidth;
const tableEditActionWidth = width < 768 ? '100' : '80';

export default function ClientInvoices() {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const [invoiceServiceContractInvoiceDateTime, setInvoiceServiceContractInvoiceDateTime] = useState(false);
  const [isOpenInvoiceServiceContract, setIsOpenInvoiceServiceContract] = useState(false);
  const [isOpenInvoice, setIsOpenInvoice] = useState(false);
  const [isOpenInvoicePos, setIsOpenInvoicePos] = useState(false);
  const [showInvoices, setShowInvoices] = useState([0]);

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const clientId = useSelector(state => state.clientRegistration.basicInfo.clientId);
  const { list, isLoading, invoiceClientName, clientTypeId } = useSelector(state => state.clientInvoices);

  const handleGoToEdit = row => {
    const { clientId, invoiceDateTime, serviceContractId } = row;

    dispatch(setClientId(clientId));
    dispatch(setInvoiceId(invoiceDateTime));

    if(+clientTypeId == 2) {
      setIsOpenInvoice(true);
      return;
    }

    if(+clientTypeId == 4 || +clientTypeId == 5) {
      setIsOpenInvoicePos(true);
      return;
    }

    if(serviceContractId) {
      setInvoiceServiceContractInvoiceDateTime(invoiceDateTime);
      setIsOpenInvoiceServiceContract(true);
      return;
    }
  }

  const handleInvoicesToggle = i => {
    if (showInvoices.includes(i)) {
      setShowInvoices(showInvoices.filter(id => id !== i));
    } else {
      setShowInvoices(showInvoices.concat(i));
    }

    return { showInvoices };
  }

  const handleInvoiceSituation = (opened, situation) => {
    const isInvoiceOpened = opened == 1;
    const isInvoicePaid = situation == 'Paga';

    return !isInvoiceOpened && !isInvoicePaid
      ? `Fechada/${situation}`
      : situation;
  }

  const convertFormattedMoneyToNumber = formattedAmount => {
    return +formattedAmount?.replace(/[R$ ]/g, '')?.replace(/[,]/g, '.')?.replace(/[.](?=.*[.])/g, '') || 0;
  }

  useEffect(() => {
    dispatch(loadFilters(userId, establishmentId));
    dispatch(handleUpdateInvoice(false));
    dispatch(loadClientInvoices(userId, establishmentId, clientId));
  }, []);

  return (
    <div className="py-4">
      <Loader spinner isLoading={isLoading}>
        {list.length > 0 ? (
          <>
            <Typography paragraph variant="h5" color="primary">
              Faturas do cliente: {invoiceClientName}
            </Typography>
            {list.map((year, i) => {
              const currentYear = path([0, 'dueDateTime'], year).split('/')[2];
              const currentInvoice = showInvoices.includes(i);

              const invoiceData = year?.map(invoice => {
                return {
                  ...invoice,
                  invoiceSituation: clientTypeId === 4 || clientTypeId === 5
                    ? handleInvoiceSituation(invoice.opened, invoice.invoiceSituation)
                    : invoice.invoiceSituation
                }
              });

              return (
                <Card>
                  <CardHeader
                    style={{ backgroundColor: '#022A5C' }}
                    title={
                      <Button
                        fullWidth
                        disableElevation
                        color="inherit"
                        style={{ textDecoration: 'none', color: 'white' }}
                        onClick={() => handleInvoicesToggle(i)}
                      >
                        <span
                          className={classes.root}
                          style={{ fontSize: '1.3rem', marginRight: '8px' }}
                        >
                          {currentYear}
                        </span>
                        <FontAwesomeIcon icon={faChevronDown} color="white" />
                      </Button>
                    }
                  />
                  <Collapse in={currentInvoice} key={i}>
                    <CardContent>
                      {isSm && (
                        <TableMobile
                          loading={isLoading}
                          data={invoiceData?.map(item => {
                            return {
                              ...item,
                              dueDateTime: {
                                index: 1,
                                label: 'Data do vencimento',
                                value: <Typography color='textSecondary' variant='subtitle2'>{item?.dueDateTime}</Typography>
                              },
                              ...(+clientTypeId == 4 || +clientTypeId == 5) && {
                                closingDateTime: {
                                  index: 2,
                                  label: 'Data do fechamento',
                                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.closingDateTime}</Typography>
                                }
                              },
                              paymentDateTime: {
                                index: 3,
                                label: 'Data do recebimento',
                                value: <Typography color='textSecondary' variant='subtitle2'>{item?.paymentDateTime}</Typography>
                              },
                              invoiceSituation: {
                                index: 4,
                                label: 'Situação',
                                value: <Typography color='textSecondary' variant='subtitle2'>{item?.invoiceSituation}</Typography>
                              },
                              amountTotal: {
                                index: 5,
                                label: 'Valor',
                                value: (
                                  <Typography color='textSecondary' variant='subtitle2'>
                                    {item?.invoiceSituation == 'Paga' ? item?.receivedAmount : convertToBrl(convertFormattedMoneyToNumber(item?.amount) + convertFormattedMoneyToNumber(item?.closingAmount))}
                                  </Typography>
                                )
                              }
                            }
                          })}
                          actions={[
                            {
                              action: rowData => (
                                <Tooltip placement='top' title='Editar fatura' enterTouchDelay={0}>
                                  <IconButton onClick={() => handleGoToEdit(rowData)}>
                                    <FontAwesomeIcon
                                      icon={faPen}
                                      color="#022A5C"
                                      size="xs"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )
                            },
                          ]}
                        />
                      )}
                      {!isSm && (
                        <MTable
                          loading={isLoading}
                          headers={(+clientTypeId == 4 || +clientTypeId == 5) ? invoicePosHeaders : headers}
                          data={invoiceData.map(invoice => {
                            return {
                              ...invoice,
                              amountTotal: invoice?.invoiceSituation == 'Paga'
                                ? invoice?.receivedAmount
                                : convertToBrl(convertFormattedMoneyToNumber(invoice?.amount) + convertFormattedMoneyToNumber(invoice?.closingAmount))
                            }
                          })}
                          actions={[
                            {
                              icon: () => (
                                <FontAwesomeIcon
                                  icon={faPen}
                                  color="#022A5C"
                                  size="xs"
                                />
                              ),
                              tooltip: 'Editar fatura',
                              onClick: (event, rowData) => handleGoToEdit(rowData)
                            }
                          ]}
                          actionColumnWidth={tableEditActionWidth}
                        />
                      )}
                    </CardContent>
                  </Collapse>
                </Card>
              );
            })}
          </>
        ) : (
          <Card>
            <CardHeader
              style={{ backgroundColor: '#022A5C' }}
              title={
                <Typography gutterBottom variant="h6" className={classes.root}>
                  Faturas do cliente
                </Typography>
              }
            />
            <CardContent className="text-center">
              <h5 className="text-secondary">
                Não há faturas criadas para esse cliente
              </h5>
              <p className="text-secondary">
                Configure a fatura desse cliente{' '}
                <Link to="/cadastro-de-cliente/cobrancas/">clicando aqui.</Link>
              </p>
            </CardContent>
          </Card>
        )}
      </Loader>
      {isOpenInvoice && (
        <Invoice
          isOpen={isOpenInvoice}
          setIsOpenInvoice={setIsOpenInvoice}
          establishmentId={establishmentId}
        />
      )}
      {isOpenInvoicePos &&
        <InvoicePos
          isOpen={isOpenInvoicePos}
          setIsOpenInvoicePos={setIsOpenInvoicePos}
          establishmentId={establishmentId}
        />
      }
      {isOpenInvoiceServiceContract &&
        <InvoiceServiceContract
          isOpenInvoiceServiceContract={isOpenInvoiceServiceContract}
          setIsOpenInvoiceServiceContract={setIsOpenInvoiceServiceContract}
          establishmentId={establishmentId}
          calculatedInvoiceDateTime={invoiceServiceContractInvoiceDateTime}
          isInvoicePaid={true}
        />
      }
    </div>
  );
}

ClientInvoices.defaultProps = {
  invoiceClientName: ''
}